import React, { useState, useContext, useEffect } from "react";
import { APIContext } from "../Contexts/APIContext"
import Box from "@mui/material/Box";
import InsightTable from "./InsightTable";
import InsightTableFilters from "./InsightTableFilters";
import InsightFiltersViewModel from "./ViewModels/InsightFiltersViewModel";
import InsightReportDialog from "./InsightReportDialog";
import InsightHelpDialog from "./HelpDialog/InsightHelpDialog";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from 'moment';
import 'moment/locale/en-gb';
import InsightTablePaginationBar from "./InsightTablePaginationBar";
import InsightReportTableViewModel from "./ViewModels/InsightReportTableViewModel";
import { InsightView } from "../ViewModels/InsightView";

moment.locale('en-gb');

interface IInsightContainerProps {
    client: string
    view: InsightView
}

export default function InsightContainer({ client, view }: IInsightContainerProps) {
    const { post, showMessage } = useContext(APIContext)
    const [filters, setFilters] = useState<InsightFiltersViewModel>(new InsightFiltersViewModel(client));
    const [reportTable, setReportTable] = useState<InsightReportTableViewModel>(new InsightReportTableViewModel());
    const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
    const [reportMatrixDialogOpen, setReportMatrixDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        setFilters(new InsightFiltersViewModel(client))
        setReportTable(new InsightReportTableViewModel())

        HasAnyInsights();
    }, [client])

    useEffect(() => {
        setReportTable(new InsightReportTableViewModel())
    }, [filters])

    const HasAnyInsights = () => {
        let controllerAction: string;
        switch (view) {
            case InsightView.Agent:
                controllerAction = 'HasAnyAgentInsightReport';
                break;
            case InsightView.Customer:
                controllerAction = 'HasAnyCustomerInsightReport';
                break;
            case InsightView.Operational:
                controllerAction = 'HasAnyOperationalInsightReport';
                break;
            default:
                throw new Error('Unknown InsightView');
        }
        post('Insight', `${controllerAction}?client=${client}`, null, (data) => {
            if (data == false)
                showMessage("error", "There are no reports for this client");
        });
    }

    const responseSearch = (newFilters?: InsightFiltersViewModel) => {
        let controllerAction: string;
        switch (view) {
            case InsightView.Agent:
                controllerAction = 'GetAgentInsightReportTable';
                break;
            case InsightView.Customer:
                controllerAction = 'GetCustomerInsightReportTable';
                break;
            case InsightView.Operational:
                controllerAction = 'GetOperationalInsightReportTable';
                break;
            default:
                throw new Error('Unknown InsightView');
        }
        post('Insight', controllerAction, newFilters ?? filters, (data) => setReportTable(data))
    }

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
                <InsightTableFilters
                    view={view}
                    filters={filters}
                    setFilters={setFilters}
                    responseSearch={responseSearch}
                    setMatrixDialogOpen={setReportMatrixDialogOpen} />
                {
                    selectedReportId ?
                        <InsightReportDialog
                            client={client}
                            view={view}
                            insightReportId={selectedReportId}
                            setInsightReportId={setSelectedReportId} />
                        :
                        <>
                            <Box sx={{ display: 'block', flex: 1, height: '1px' }}>
                                <InsightTable
                                    view={view}
                                    reports={reportTable.reports}
                                    setSelectedReportId={setSelectedReportId} />
                            </Box>
                            <Box sx={{ display: 'block' }}>
                                <InsightTablePaginationBar
                                    pageNumber={filters.pageNumber}
                                    pageNumberCount={reportTable.pageCount}
                                    resultsCount={reportTable.resultCount}
                                    responseSearch={responseSearch}
                                    filters={filters}
                                    setFilters={setFilters} />
                            </Box>
                        </>
                }
            </LocalizationProvider >
            <InsightHelpDialog
                client={client}
                open={reportMatrixDialogOpen}
                setOpen={setReportMatrixDialogOpen}
            />
        </>
    );
};
